import { LengthUnit } from '@fulcrum/lib';
import { t } from '@trimble/i18n';
import {
  Measurement,
  AlignmentMeasurementType,
  MeasurementInfoButtonConfig,
} from '@trimble/web3d-providers';
import { createMemo, Show, type JSX } from 'solid-js';
import {
  LengthFormatter,
  calculateHorizontalDistance,
  calculateVerticalDistance,
  formatChainageLabel,
} from '@trimble/utilities';
import {
  MeasurementProperty,
  ResultRowProps,
} from '@trimble/measurements-shared';
import type { AlignmentSettings, FormattingSettings } from '@trimble/types';

export interface AlignmentMeasurementDetailsProps {
  measurement: Measurement | undefined;
  baseLengthUnit: LengthUnit;
  formattingSettings: FormattingSettings;
  alignmentSettings?: AlignmentSettings;
  infoButtonConfig?: MeasurementInfoButtonConfig;
}

export const AlignmentMeasurementDetails = (
  props: AlignmentMeasurementDetailsProps
): JSX.Element | null => {
  const measurementData = createMemo((): AlignmentMeasurementType | null => {
    if (!props.measurement) return null;
    return props.measurement as AlignmentMeasurementType;
  });

  const alignmentData = createMemo((): ResultRowProps[] | null => {
    const data = measurementData();
    if (!data) return null;

    const horizontal = calculateHorizontalDistance(data.alignment, data.point);
    const vertical = calculateVerticalDistance(data.alignment, data.point);

    return [
      {
        cells: [
          {
            label: t('measurements.xyOffset'),
            value: LengthFormatter.toDisplay({
              measuredValue: horizontal,
              currentUnits: props.baseLengthUnit,
              settings: props.formattingSettings,
              useMeasurementSettings: true,
            }),
            testId: 'xy-offset-value',
            valueTestId: 'xy-offset-value-display',
          },
          {
            label: t('measurements.zOffset'),
            value: LengthFormatter.toDisplay({
              measuredValue: vertical,
              currentUnits: props.baseLengthUnit,
              settings: props.formattingSettings,
              useMeasurementSettings: true,
            }),
            testId: 'z-offset-value',
            valueTestId: 'z-offset-value-display',
          },
        ],
        testId: 'offset-row',
      },
      {
        cells: [
          {
            label: t('measurements.station'),
            value: props.alignmentSettings
              ? formatChainageLabel(
                  data.value,
                  props.alignmentSettings.labelFormat,
                  (props.formattingSettings.lengthUnit ?? 'm') as LengthUnit,
                  props.formattingSettings.lengthDecimals
                )
              : LengthFormatter.toDisplay({
                  measuredValue: data.value,
                  currentUnits: props.baseLengthUnit,
                  settings: props.formattingSettings,
                  useMeasurementSettings: false,
                }),
            testId: 'station-value',
            valueTestId: 'station-value-display',
          },
        ],
        testId: 'station-row',
      },
    ];
  });

  return (
    <Show when={alignmentData()}>
      {(data) => (
        <div
          class="flex flex-col gap-3 p-3"
          data-testid="alignment-measurement-details"
        >
          <MeasurementProperty
            icon="tc-icon-path-navigatio"
            title={t('web3D.toolbar.actions.alignmentMeasurement')}
            resultRows={data()}
            testId="alignment-section"
            infoButtonConfig={props.infoButtonConfig}
          />
        </div>
      )}
    </Show>
  );
};
